<template>
  <div>
    <add-article @add="add" />

    <v-dialog v-model="display" max-width="500" transition="dialog-bottom-transition" persistent>
      <v-card outlined class="modern-card overflow-hidden">
        <!-- Premium Header -->
        <v-sheet color="primary" class="pa-6 d-flex align-center">
          <v-avatar size="44" color="white opacity-20" class="mr-4 shadow-sm">
            <v-icon color="white">fas fa-boxes</v-icon>
          </v-avatar>
          <div>
            <div class="caption white--text opacity-80 font-weight-black text-uppercase tracking-widest mb-1">Registro de Consumo</div>
            <div class="text-h5 white--text font-weight-black lh-1-2">Agregar Material</div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon color="white" large @click="display = false">
            <v-icon>fas fa-times</v-icon>
          </v-btn>
        </v-sheet>

        <v-card-text class="pa-6 bg-white">
          <!-- Selected Article Info Card -->
          <div v-if="selected" class="mb-6">
            <v-card outlined class="pa-4 bg-grey-light border-light modern-inner-card overflow-hidden elevation-0">
               <div class="d-flex align-center mb-3">
                 <v-chip color="primary" label x-small class="font-weight-black mr-3">SKU: {{selected.sku}}</v-chip>
                 <span class="body-2 font-weight-black grey--text text--darken-4 text-truncate">{{selected.name}}</span>
               </div>
               
               <v-divider class="my-3"></v-divider>
               
               <v-row dense>
                 <v-col cols="6">
                   <div class="caption grey--text font-weight-black text-uppercase mb-1 tracking-wider">Unidad Consumo</div>
                   <div class="body-2 font-weight-bold primary--text">
                     <v-icon x-small color="primary" class="mr-1">fas fa-weight-hanging</v-icon>
                     {{selected.unit_consume_name}}
                   </div>
                 </v-col>
                 <v-col cols="6">
                   <div class="caption grey--text font-weight-black text-uppercase mb-1 tracking-wider">Precio Unit. (Prom)</div>
                   <div class="body-2 font-weight-bold success--text">
                     {{selected.avg | currency('$',0)}} / {{selected.unit_name}}
                   </div>
                 </v-col>
               </v-row>
               
               <div class="mt-3 pa-3 bg-primary-light rounded border-primary-light d-flex align-center">
                  <v-icon x-small color="primary" class="mr-2">fas fa-calculator</v-icon>
                  <span class="caption primary--text font-weight-bold">Factor de Conversión: <strong>{{selected.unit_consume_factor}}</strong></span>
               </div>
            </v-card>
          </div>

          <ValidationObserver v-slot="{ handleSubmit }">
            <v-form @submit.prevent="handleSubmit(confirmation)" class="mt-2">
              <v-checkbox
                v-model="reprocess"
                label="Registrar como consumo por REPROCESO"
                dense
                color="error"
                class="mt-0 pt-0 mb-4"
              >
                <template v-slot:label>
                  <span class="body-2 font-weight-bold" :class="reprocess ? 'error--text' : 'grey--text text--darken-2'">
                    Registrar como consumo por REPROCESO
                  </span>
                </template>
              </v-checkbox>

              <ValidationProvider name="Cantidad" rules="required|minvalue:0" v-slot="{ errors }">
                <v-text-field
                  ref="qtyField"
                  type="number"
                  v-model="qty"
                  label="Cantidad a consumir"
                  placeholder="0.00"
                  outlined
                  dense
                  required
                  persistent-placeholder
                  :suffix="selected ? selected.unit_consume_name : ''"
                  :error-messages="errors"
                  class="modern-input"
                />
              </ValidationProvider>

              <v-row dense class="mt-6 mx-0">
                <v-col cols="6" class="pl-0 pr-2">
                  <v-btn text block class="text-none grey--text font-weight-bold" @click="display = false">
                    Cancelar
                  </v-btn>
                </v-col>
                <v-col cols="6" class="pr-0 pl-2">
                  <v-btn color="success" block dark depressed class="text-none font-weight-black shadow-sm" type="submit">
                    <v-icon left x-small>fas fa-check-circle</v-icon> Guardar
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </ValidationObserver>
        </v-card-text>
      </v-card>
    </v-dialog>

    <confirm
      v-model="displayConfirmation"
      :message="confirmationMessage"
      @ok="save"
      @cancel="displayConfirmation = false"
    />
  </div>
</template>

<script>
import AddArticle from '@/components/stocks/AddArticle'
import { createComsumptionBitacora } from '@/api/production'
import notifications from '@/utils/notifications'

export default {
  name: 'AddConsumption',
  components: { AddArticle },
  props: ['productId', 'work_order_id'],
  data() {
    return {
      display: false,
      displayConfirmation: false,
      selected: null,
      qty: null,
      reprocess: false,
      confirmationMessage: ''
    }
  },
  methods: {
    add(item) {
      this.selected = item
      if (!this.selected.unit_consume_factor) this.selected.unit_consume_factor = 1
      if (!this.selected.unit_consume_name) this.selected.unit_consume_name = this.selected.unit_name
      this.qty = 1
      this.display = true
    },
    confirmation() {
      this.confirmationMessage = `¿Desea registrar el consumo de [${this.selected.sku}] ${this.selected.name} por la cantidad de ${this.qty} ${this.selected.unit_consume_name.toUpperCase()}?`
      this.displayConfirmation = true
    },
    async save() {
      this.displayConfirmation = false
      const consumption = {
        article_id: this.selected.id,
        product_id: this.productId,
        articlePrice: parseFloat(this.selected.avg),
        convertedPrice: parseFloat(this.selected.avg) * parseFloat(this.selected.unit_consume_factor),
        qty: this.qty,
        unit: this.selected.unit_name,
        unitConsume: this.selected.unit_consume_name,
        unitConsume_factor: parseFloat(this.selected.unit_consume_factor),
        reprocess: this.reprocess,
      }
      
      try {
        await createComsumptionBitacora(consumption)
        notifications.showSuccess('Consumo agregado exitosamente')
        this.$emit('added')
        this.display = false
        this.selected = null
      } catch (error) {
        notifications.showError(error)
      } finally {
        await this.$store.dispatch('fetchWorkOrderResume', this.work_order_id)
        this.reprocess = false
      }
    }
  }
}
</script>

<style scoped>
.modern-card {
  border: 1px solid rgba(0,0,0,0.08) !important;
  border-radius: 12px !important;
}
.modern-inner-card {
  border-radius: 12px !important;
}
.border-light {
  border: 1px solid rgba(0,0,0,0.05) !important;
}
.bg-grey-light { background-color: #f8f9fa; }
.bg-primary-light { background-color: rgba(var(--v-primary-base), 0.05); }
.border-primary-light { border: 1px solid rgba(var(--v-primary-base), 0.1); }
.lh-1-2 { line-height: 1.2; }
.tracking-widest { letter-spacing: 2px; }
.tracking-wider { letter-spacing: 1px; }
.shadow-sm { box-shadow: 0 2px 4px rgba(0,0,0,0.08) !important; }
</style>